import { createClient, createConfig } from '@hey-api/client-fetch'
import { runtimeConfig } from '@/shared/config'
import { ApiError } from './errors'

// Единая линия HTTP-клиента (Hey API client-fetch). Cookie-auth:
// credentials:'include' гонит httpOnly-cookie сессии (Domain=.stit.tech,
// 00-architecture § 3) в каждый запрос — сотрудника (stit_session) или заказчика
// (stit_customer), какая есть. CSRF-заголовков нет — защита mutating-запросов
// держится на SameSite=Lax + same-site мутациях. Все ручки бэка под /api.
export const apiClient = createClient(
  createConfig({
    baseUrl: '',
    credentials: 'include',
  }),
)

const API_PREFIX = runtimeConfig.API_BASE_URL

// Событие «hub-сессия кончилась» — 401, переживший попытку refresh. Слушает
// AuthGate в _app и сам решает, уводить ли на SSO. Навигации здесь нет
// намеренно: клиент общий с публичным booking-контуром (/booking/$token вне
// _app), и редирект отсюда утащил бы анонимного гостя на SSO с публичной
// страницы.
export const UNAUTHORIZED_EVENT = 'api:unauthorized'

// Контуров сессии два (00-architecture § 3), и различить их из JS нечем: обе
// cookie httpOnly. Поэтому фронт помнит контур, который ответил на identity, и
// продлевает им. Это подсказка «куда идти первым», а не идентификация: ни
// логина, ни прав в ней нет, решает всё равно cookie.
export type SessionKind = 'employee' | 'customer'

const KIND_KEY = 'auth:kind'

function readStoredKind(): SessionKind | null {
  try {
    const raw = localStorage.getItem(KIND_KEY)
    return raw === 'employee' || raw === 'customer' ? raw : null
  } catch {
    // Приватный режим/заблокированное хранилище — работаем без подсказки.
    return null
  }
}

let lastKind: SessionKind | null = readStoredKind()

export function rememberSessionKind(kind: SessionKind | null): void {
  lastKind = kind
  try {
    if (kind) localStorage.setItem(KIND_KEY, kind)
    else localStorage.removeItem(KIND_KEY)
  } catch {
    // см. readStoredKind — контур останется в памяти вкладки
  }
}

export function lastSessionKind(): SessionKind | null {
  return lastKind
}

// Access-cookie обоих контуров живёт минуты, живость сессии держит refresh-cookie:
// у сотрудника на id.stit.tech (Path=/auth), у заказчика на api.hub.stit.tech
// (Path=/auth/customer). Обе httpOnly, так что из JS не видно ни срока access, ни
// наличия refresh: отличить «протухла минуту назад» от «сессии нет» можно только
// походом в refresh-ручку. Ходим мимо apiClient — 401 самого refresh не должен
// заходить на второй круг.
function refreshUrl(kind: SessionKind): string {
  return kind === 'customer'
    ? `${runtimeConfig.HUB_API_BASE_URL}/auth/customer/refresh`
    : `${runtimeConfig.ID_BASE_URL}/auth/refresh`
}

function refreshOne(kind: SessionKind): Promise<boolean> {
  return fetch(refreshUrl(kind), { method: 'POST', credentials: 'include' })
    .then((res) => res.ok)
    .catch(() => false)
}

// Порядок попыток решает последний известный контур; на холодном старте (или
// когда подсказка соврала) пробуем оба — сессия есть ровно в одном, и только
// отказ обоих означает «идти на вход». Employee первым: он и частотнее, и
// главнее при обеих cookie (приоритет из hubkit/sso).
async function runRefresh(): Promise<boolean> {
  const order: SessionKind[] =
    lastKind === 'customer' ? ['customer', 'employee'] : ['employee', 'customer']
  for (const kind of order) {
    if (await refreshOne(kind)) {
      rememberSessionKind(kind)
      expired = false
      return true
    }
  }
  // Контур был известен, а продлить не вышло — это истечение сессии, а не «сюда
  // впервые». Отличие видно только здесь, и от него зависит notice на входе.
  if (lastKind) expired = true
  return false
}

let expired = false

/** Сессия была и умерла (а не «не заходил») — единый вход покажет notice=expired. */
export function sessionExpired(): boolean {
  return expired
}

let refreshInFlight: Promise<boolean> | null = null

export function refreshSession(): Promise<boolean> {
  // Refresh ротирующий: параллельные попытки погасили бы друг друга и выбросили
  // живого пользователя, поэтому все одновременно прилетевшие 401 ждут один и
  // тот же промис — независимо от того, какой контур он в итоге продлит.
  refreshInFlight ??= runRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

// Выход заказчика гасит сессию там, где она выпущена, — в хабе (своей cookie
// заказчику календарь не выдаёт). Мимо apiClient: чужой origin и свой корень.
export function logoutCustomerSession(): Promise<void> {
  return fetch(`${runtimeConfig.HUB_API_BASE_URL}/auth/customer/logout`, {
    method: 'POST',
    credentials: 'include',
  })
    .then(() => undefined)
    .catch(() => undefined)
}

// Identity заказчика живёт у issuer'а его сессии: своей ручки про заказчика у
// календаря нет (его customer-поверхность — только календарь компании и
// событие). Запрос идёт мимо apiClient — чужой origin и свой корень; 401
// проходит через то же single-flight продление, что и остальной API.
export async function fetchCustomerIdentity(): Promise<unknown> {
  const call = () =>
    fetch(`${runtimeConfig.HUB_API_BASE_URL}/auth/customer/me`, {
      credentials: 'include',
      headers: { accept: 'application/json' },
    })
  try {
    let res = await call()
    if (res.status === 401 && (await refreshSession())) res = await call()
    if (!res.ok) return null
    // Dev-сервер на неперехваченном пути отдаёт index.html с 200 — HTML-ответ
    // это «ручки нет», а не identity.
    if (!(res.headers.get('content-type') ?? '').includes('application/json')) return null
    return await res.json()
  } catch {
    return null
  }
}

// Зона устройства сотрудника нужна там, где экрана нет: текст телеграм-алерта и
// сетка брони для гостя считаются на сервере и читают проекцию
// hub_employees.timezone. Держит проекцию хаб, а живёт человек в календаре —
// поэтому зону устройства хабу сообщает эта SPA. Запись молчаливая: ответ не
// нужен, а отказ не эскалируется — следующая загрузка догонит; цена — алерт
// узнаёт о перелёте не мгновенно.
//
// Мимо apiClient: ручка хаба (ID_BASE_URL), свой префикс /api — не календарный
// API_BASE_URL, и своя hub-cookie, отсюда credentials. Идемпотентна: на
// совпавшей зоне хаб не пишет вовсе, сверять на клиенте нечего.
export function reportDeviceTimezone(timezone: string): Promise<void> {
  return fetch(`${runtimeConfig.ID_BASE_URL}/api/employees/me/timezone`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ timezone }),
  })
    .then(() => undefined)
    .catch(() => undefined)
}

interface RequestOpts {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  url: string
  path?: Record<string, unknown>
  query?: Record<string, unknown>
  body?: Record<string, unknown> | unknown[]
  headers?: Record<string, string>
}

// Запрос к /api/* (huma). entities/<slice>/api оборачивают это в
// queryOptions/мутации вручную; конверт `{ data }` разворачивает вызывающий.
// Сессионный контур живёт здесь, а не в interceptor'е: повтор строится из тех же
// опций, тогда как отправленный Request уже отдал своё тело в сеть.
export async function apiRequest<T>(opts: RequestOpts): Promise<T> {
  const req = { ...opts, url: `${API_PREFIX}${opts.url}` }
  let result = await apiClient.request<T, unknown>(req)
  // Одна попытка: refresh либо продлевает сессию (тогда повтор проходит), либо
  // она кончилась и второй заход даст тот же 401.
  if (result.response.status === 401 && (await refreshSession())) {
    result = await apiClient.request<T, unknown>(req)
  }
  const { data, error, response } = result
  if (response.status === 401) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
  if (!response.ok || error !== undefined) {
    throw ApiError.from(response, error)
  }
  return data as T
}

/** Конверт ответов huma: полезная нагрузка в поле `data`. */
export interface DataEnvelope<T> {
  data: T
}
